import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import React, { useEffect, useState } from 'react'
import apiService from '../../api/apiService'

const RecipeSelectionModal = ({ isOpen, onClose, onSelectRecipe, dayInfo }) => {
  const [recipes, setRecipes] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [selectedRecipe, setSelectedRecipe] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        setLoading(true)
        setError(null)

        const data = await apiService.getRecipesFull()
        setRecipes(data || [])
      } catch (error) {
        console.error('Error fetching recipes:', error)
        setError(error.message)
        setRecipes([])
      } finally {
        setLoading(false)
      }
    }

    if (isOpen) {
      fetchRecipes()
      setSelectedRecipe(null) // Reset selected recipe when modal opens
    }
  }, [isOpen])

  const filteredRecipes = recipes.filter((recipe) =>
    recipe.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleRecipeClick = (recipe) => {
    // Set selected state for immediate visual feedback
    setSelectedRecipe(recipe.id)

    // Call the onSelectRecipe callback
    // This will typically close the modal in the parent component
    onSelectRecipe(recipe)
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50 modal">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="mx-auto max-w-sm w-full rounded-xl bg-white shadow-xl">
          <div className="relative">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <DialogTitle className="text-lg font-semibold">
                Add Recipe
              </DialogTitle>
              <button
                onClick={onClose}
                className="p-1 rounded-full hover:bg-gray-100"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Search */}
            <div className="p-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Recipe List */}
            <div className="max-h-[60vh] overflow-y-auto">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <div className="text-red-500 mb-2">Error loading recipes</div>
                  <div className="text-sm text-gray-500">{error}</div>
                </div>
              ) : filteredRecipes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchQuery
                    ? 'No recipes match your search'
                    : 'No recipes found'}
                </div>
              ) : (
                <div className="flex flex-col">
                  {filteredRecipes.map((recipe) => (
                    <button
                      key={recipe.id}
                      onClick={() => handleRecipeClick(recipe)}
                      disabled={selectedRecipe !== null}
                      className={`flex p-3 ${
                        selectedRecipe === recipe.id
                          ? 'bg-green-50'
                          : 'hover:bg-gray-50'
                      } rounded-lg text-left transition-colors ${
                        selectedRecipe !== null && selectedRecipe !== recipe.id
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      <div className="flex items-center w-full">
                        {recipe.recipe_images &&
                          recipe.recipe_images.length > 0 && (
                            <img
                              src={recipe.recipe_images[0].image_url}
                              alt={recipe.name}
                              className="w-16 h-16 rounded-md object-cover object-center flex-shrink-0 mr-4"
                            />
                          )}
                        <span className="font-medium flex-grow w-full truncate">
                          {recipe.name}
                        </span>
                        {selectedRecipe === recipe.id && (
                          <div className="text-green-600 flex-shrink-0 ml-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default RecipeSelectionModal
